import { Box, CircularProgress, Divider, Grid, Button } from "@mui/material";
import { OpenInNew } from "@mui/icons-material";
import { makeStyles } from "tss-react/mui";
import { VFC } from "react";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "../../../providers/Auth/auth.provider";
import { AvailablePlatform } from "../../../providers/Auth/platform.provider";
import config from "../../../config/config";
import { Shades } from "../../../style/Colors";
import { SCROLL_TO_TOP_BUTTON_HEIGHT } from "../../../utils/constants";
import { FormHeader } from "../../../components/form/form-header.component";
import { GroupContractsTable } from "../../../components/table/group-contracts-table";
import { isAdmin, isPomMainContact } from "../../../domain/user/user";
import { useGetGroupForDetailsQuery } from "../../../repositories/queries/group/get-group-for-details.query";
import { GroupDetailProvider } from "../group-detail.provider";
import { GroupDetailBaseInfo } from "./group-detail-base-info.component";
import { GroupDetailTypeInfo } from "./group-detail-type-info.component";
import { GroupDetailsUsersTable } from "./tables/group-detail-users-table.component";

const useStyles = makeStyles()(() => ({
  alignSpinner: {
    textAlign: "center",
  },
  divider: {
    backgroundColor: Shades.gray20,
    marginTop: 0,
    marginBottom: 0,
  },
  spacingBottom: {
    marginBottom: SCROLL_TO_TOP_BUTTON_HEIGHT,
  },
}));

interface IGroupDetailContentProps {
  erpNumber: string;
  platform: AvailablePlatform;
  createUserOnLoad: boolean;
}

export const GroupDetailContent: VFC<IGroupDetailContentProps> = (props) => {
  const { erpNumber } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { internalUser } = useAuthContext();

  const { isLoading, data: group } = useGetGroupForDetailsQuery(erpNumber);

  const displayContracts = isAdmin(internalUser) || isPomMainContact(internalUser);
  const displayGroupDetailType = isAdmin(internalUser);
  const bcEditMasterDataUrl = config.bcEditMasterDataUrl;

  return (
    <>
      <Grid container direction="column" spacing={2}>
        <Grid item>
          <FormHeader>{t("basedata.groups.detail.text", { erpNumber: erpNumber })}</FormHeader>
        </Grid>
        {isLoading && (
          <Grid item>
            <Grid container direction="row">
              <Grid item md={12} className={classes.alignSpinner}>
                <CircularProgress />
              </Grid>
            </Grid>
          </Grid>
        )}
        {!isLoading && group && (
          <GroupDetailProvider group={group}>
            <Grid item>
              <GroupDetailBaseInfo group={group} headerOnly={false} />
            </Grid>
            <Grid item>
              <Divider className={classes.divider} />
            </Grid>
            {displayGroupDetailType && (
              <>
                <Grid item>
                  <GroupDetailTypeInfo group={group} />
                </Grid>
                <Grid item>
                  <Divider className={classes.divider} />
                </Grid>
              </>
            )}

            {/* tables */}
            {displayContracts && (
              <>
                <Grid item>
                  <GroupContractsTable readOnly={!isAdmin(internalUser)} />
                </Grid>
                <Grid item>
                  <Divider className={classes.divider} />
                </Grid>
              </>
            )}
            <Grid item>
              <GroupDetailsUsersTable group={group} />
            </Grid>
            <Grid item>
              <Divider className={classes.divider} />
            </Grid>
          </GroupDetailProvider>
        )}
        {isPomMainContact(internalUser) && bcEditMasterDataUrl && (
          <Grid item>
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="outlined"
                href={bcEditMasterDataUrl}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<OpenInNew />}
              >
                {t("basedata.groups.detail.bcEditButton")}
              </Button>
            </Box>
          </Grid>
        )}
        <Box className={classes.spacingBottom}></Box>
      </Grid>
    </>
  );
};
